import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { useGridGeometry } from '../../hooks/useGridGeometry'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { cn } from '../../lib/cn'
import type { GridGeometry } from '../../utils/gridGeometry'

// Width = (cols * TILE_SIZE) + 1px (left border)
// medium: 30 × 32 + 1 = 961px  |  small: 24 × 32 + 1 = 769px
// Tailwind requires static class names; update these manually if GRID_CONFIG or TILE_SIZE change.
// The `full` variant fills its container via a CSS background pattern instead
// of tile elements; it ignores animateOnMount.
export const gridBackgroundVariants = cva('overflow-hidden', {
  variants: {
    variant: {
      medium: 'w-[961px]',
      small: 'w-[769px]',
      large: 'w-[961px]',
      'x-large': 'w-[961px]',
      'xx-large': 'w-[961px]',
      full: 'h-full w-full',
      fluid: 'w-full',
    },
  },
  defaultVariants: {
    variant: 'medium',
  },
})

export type GridBackgroundProps = Omit<
  React.ComponentPropsWithRef<'div'>,
  'children'
> &
  VariantProps<typeof gridBackgroundVariants> & {
    animateOnMount?: boolean
    onAnimationComplete?: () => void
    geometry?: GridGeometry
    cols?: number
    rows?: number
    minCols?: number
  }

const GRID_CONFIG = {
  medium: { cols: 30, rows: 16 },
  small: { cols: 24, rows: 13 },
  large: { cols: 30, rows: 20 },
  'x-large': { cols: 30, rows: 24 },
  'xx-large': { cols: 30, rows: 32 },
} as const

type TileVariant = keyof typeof GRID_CONFIG

const TILE_SIZE = 32

// Each step defines how many cols×rows are visible at that point in the animation.
const ANIMATION_STEPS = [
  { cols: 1, rows: 1 },
  { cols: 2, rows: 2 },
  { cols: 3, rows: 2 },
  { cols: 4, rows: 2 },
  { cols: 7, rows: 3 },
  { cols: 11, rows: 3 },
  { cols: 15, rows: 5 },
  { cols: 19, rows: 7 },
  { cols: 22, rows: 9 },
  { cols: 24, rows: 11 },
  { cols: 27, rows: 12 },
  { cols: 29, rows: 15 },
  { cols: 30, rows: 16 },
  { cols: 30, rows: 20 },
  { cols: 30, rows: 24 },
  { cols: 30, rows: 32 },
] as const

// Duration in milliseconds between each step reveal
const STEP_DURATION_MS = 100

const useGridReveal = ({
  cols,
  rows,
  tileSize,
  animateOnMount,
  onAnimationComplete,
}: {
  cols: number
  rows: number
  tileSize: number
  animateOnMount: boolean
  onAnimationComplete?: () => void
}): string | undefined => {
  // The first ANIMATION_STEPS index where this variant is fully revealed.
  // For `small` (24×13) this is step 11, not the final step 12, avoiding
  // unnecessary re-renders once the grid is already completely visible.
  const effectiveLastStep = useMemo(() => {
    for (let i = 0; i < ANIMATION_STEPS.length; i++) {
      const { cols: stepCols, rows: stepRows } = ANIMATION_STEPS[i]
      if (
        Math.min(stepCols, cols) === cols &&
        Math.min(stepRows, rows) === rows
      ) {
        return i
      }
    }
    return ANIMATION_STEPS.length - 1
  }, [cols, rows])

  const prefersReducedMotion = useReducedMotion()

  const [step, setStep] = useState(() =>
    animateOnMount && !prefersReducedMotion ? 0 : effectiveLastStep,
  )

  useEffect(() => {
    if (!animateOnMount || prefersReducedMotion || step >= effectiveLastStep)
      return undefined
    const timer = setTimeout(() => setStep((s) => s + 1), STEP_DURATION_MS)
    return () => clearTimeout(timer)
  }, [animateOnMount, prefersReducedMotion, step, effectiveLastStep])

  const completedRef = useRef(false)

  useEffect(() => {
    if (
      animateOnMount &&
      step >= effectiveLastStep &&
      onAnimationComplete &&
      !completedRef.current
    ) {
      completedRef.current = true
      onAnimationComplete()
    }
  }, [animateOnMount, step, effectiveLastStep, onAnimationComplete])

  if (!animateOnMount || prefersReducedMotion || step >= effectiveLastStep) {
    return undefined
  }

  const { cols: revealedCols, rows: revealedRows } = ANIMATION_STEPS[step]
  const clampedCols = Math.min(revealedCols, cols)
  const clampedRows = Math.min(revealedRows, rows)
  const rightClip = Math.max(0, (cols - clampedCols) * tileSize)
  const bottomClip = Math.max(0, (rows - clampedRows) * tileSize)
  return `inset(0 ${rightClip}px ${bottomClip}px 0)`
}

interface TileGridBackgroundProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  variant: TileVariant
  animateOnMount: boolean
  onAnimationComplete?: () => void
}

/** Tile-based grid renderer. Fixed-width variants only — uses a flex grid of
 *  32×32 divs and supports the staggered animateOnMount reveal. */
const TileGridBackground = forwardRef<HTMLDivElement, TileGridBackgroundProps>(
  (
    { variant, animateOnMount, onAnimationComplete, className, ...props },
    ref,
  ) => {
    const { cols, rows } = GRID_CONFIG[variant]
    const total = cols * rows
    const tiles = useMemo(
      () => Array.from({ length: total }, (_, i) => i),
      [total],
    )

    const clipPath = useGridReveal({
      cols,
      rows,
      tileSize: TILE_SIZE,
      animateOnMount,
      onAnimationComplete,
    })

    return (
      <div
        ref={ref}
        aria-hidden='true'
        role='presentation'
        className={cn(
          gridBackgroundVariants({ variant }),
          'pointer-events-none',
          className,
        )}
        {...props}
      >
        <div
          className='flex flex-wrap border-t border-l border-edu-grid-line'
          style={clipPath !== undefined ? { clipPath } : undefined}
        >
          {tiles.map((i) => (
            <div
              key={i}
              className='size-8 shrink-0 border-r border-b border-edu-grid-line'
            />
          ))}
        </div>
      </div>
    )
  },
)

TileGridBackground.displayName = 'TileGridBackground'

// `--edu-stroke-subtle` is not a global token, but hosts set it per instance
// to retint a grid (StatusPortalPage, PaymentPlanPage), so it must stay the
// first lookup. Unset, the grid falls through to the grid-line token.
const GRID_LINE_COLOR = 'var(--edu-stroke-subtle, var(--color-edu-grid-line))'

/** CSS-pattern grid renderer. Fills its container regardless of size and
 *  doesn't support animateOnMount — used for full-bleed grid backgrounds
 *  (e.g. behind the homepage Up Next carousel). */
const FullGridBackground = forwardRef<
  HTMLDivElement,
  Omit<React.ComponentPropsWithRef<'div'>, 'children'>
>(({ className, style, ...props }, ref) => (
  <div
    ref={ref}
    aria-hidden='true'
    role='presentation'
    className={cn(
      gridBackgroundVariants({ variant: 'full' }),
      'pointer-events-none',
      className,
    )}
    {...props}
    style={{
      backgroundImage: `linear-gradient(to right, ${GRID_LINE_COLOR} 1px, transparent 1px), linear-gradient(to bottom, ${GRID_LINE_COLOR} 1px, transparent 1px)`,
      backgroundSize: `${TILE_SIZE}px ${TILE_SIZE}px`,
      // The gradient draws lines at the start of each cell (0, 32, ...) so
      // the bottom edge of the lowest row has no closing line. Inset shadow
      // adds it without altering box dimensions or shifting the pattern.
      boxShadow: `inset 0 -1px 0 ${GRID_LINE_COLOR}`,
      // Consumer style merges on top so they can extend (e.g. add an
      // opacity or override boxShadow) without wiping the grid pattern.
      ...style,
    }}
  />
))

FullGridBackground.displayName = 'FullGridBackground'

interface FluidGridBackgroundProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  geometry?: GridGeometry
  cols: number
  rows: number
  minCols?: number
  animateOnMount: boolean
  onAnimationComplete?: () => void
}

const GridLines = ({
  cols,
  rows,
  tileSize,
}: {
  cols: number
  rows: number
  tileSize: number
}) => (
  <>
    {Array.from({ length: cols }, (_, i) => (
      <div
        key={`col-${i}`}
        className='absolute inset-y-0 w-px'
        style={{ left: i * tileSize, backgroundColor: GRID_LINE_COLOR }}
      />
    ))}
    {Array.from({ length: rows }, (_, i) => (
      <div
        key={`row-${i}`}
        className='absolute inset-x-0 h-px'
        style={{ top: i * tileSize, backgroundColor: GRID_LINE_COLOR }}
      />
    ))}
  </>
)

const FluidGridBackground = forwardRef<
  HTMLDivElement,
  FluidGridBackgroundProps
>(
  (
    {
      geometry,
      cols,
      rows,
      minCols,
      animateOnMount,
      onAnimationComplete,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const { ref: measureRef, geometry: measuredGeometry } = useGridGeometry({
      cols,
      rows,
      minCols,
      fixed: geometry !== undefined,
    })
    const resolved = geometry ?? measuredGeometry

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        measureRef(node)
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      },
      [measureRef, ref],
    )

    const clipPath = useGridReveal({
      cols: resolved.cols,
      rows: resolved.rows,
      tileSize: resolved.tileSize,
      animateOnMount,
      onAnimationComplete,
    })

    const boardStyle: React.CSSProperties = {
      width: resolved.width,
      height: resolved.height,
      boxShadow: `inset -1px 0 0 ${GRID_LINE_COLOR}, inset 0 -1px 0 ${GRID_LINE_COLOR}`,
      ...(clipPath !== undefined ? { clipPath } : null),
    }

    const lines = (
      <GridLines
        cols={resolved.cols}
        rows={resolved.rows}
        tileSize={resolved.tileSize}
      />
    )

    if (geometry !== undefined) {
      return (
        <div
          ref={ref}
          aria-hidden='true'
          role='presentation'
          className={cn(
            'relative overflow-hidden pointer-events-none',
            className,
          )}
          {...props}
          style={{ ...boardStyle, ...style }}
        >
          {lines}
        </div>
      )
    }

    return (
      <div
        ref={setRefs}
        className={cn(
          gridBackgroundVariants({ variant: 'fluid' }),
          'pointer-events-none',
          className,
        )}
        {...props}
        style={style}
      >
        <div
          aria-hidden='true'
          role='presentation'
          className='relative overflow-hidden pointer-events-none'
          style={boardStyle}
        >
          {lines}
        </div>
      </div>
    )
  },
)

FluidGridBackground.displayName = 'FluidGridBackground'

export const GridBackground = forwardRef<HTMLDivElement, GridBackgroundProps>(
  (
    {
      variant = 'medium',
      animateOnMount = false,
      onAnimationComplete,
      geometry,
      cols = 30,
      rows = 16,
      minCols,
      ...props
    },
    ref,
  ) => {
    const resolvedVariant = variant ?? 'medium'
    if (resolvedVariant === 'full') {
      return <FullGridBackground ref={ref} {...props} />
    }
    if (resolvedVariant === 'fluid') {
      return (
        <FluidGridBackground
          ref={ref}
          geometry={geometry}
          cols={cols}
          rows={rows}
          minCols={minCols}
          animateOnMount={animateOnMount}
          onAnimationComplete={onAnimationComplete}
          {...props}
        />
      )
    }
    return (
      <TileGridBackground
        ref={ref}
        variant={resolvedVariant}
        animateOnMount={animateOnMount}
        onAnimationComplete={onAnimationComplete}
        {...props}
      />
    )
  },
)

GridBackground.displayName = 'GridBackground'
